import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  CardContent,
  Box,
  TextField,
  List,
  ListItem,
  Avatar,
  Typography,
} from "@mui/material";
import { Send } from "@mui/icons-material";
import Button from "../../components/Button/Button";
import EmptyChatSvg from "../../assets/images/empty_chat_icon.svg";
import ChatBotSvg from "../../assets/images/chat_bot.svg";
import DashboardStyles from "./Dashboard.module.scss";

export interface ChatMessage {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

interface ChatProps {
  messages: ChatMessage[];
  onSendMessage: (message: string) => void;
  isExpanded?: boolean;
  isLoading?: boolean;
  suggestions?: string[];
}

const SUGGESTION_WINDOW = 3;

const Chat: React.FC<ChatProps> = ({
  messages,
  onSendMessage,
  isExpanded = false,
  isLoading = false,
  suggestions = [],
}) => {
  const [inputValue, setInputValue] = useState("");
  const [suggestionIndex, setSuggestionIndex] = useState(0);

  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = () => {
    if (inputValue.trim()) {
      onSendMessage(inputValue);
      setInputValue("");
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (isLoading) return;
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    onSendMessage(suggestion);
  };

  const visibleSuggestions = suggestions.slice(
    suggestionIndex,
    suggestionIndex + SUGGESTION_WINDOW
  );

  const handleShowMore = () => {
    setSuggestionIndex((prev) =>
      prev + SUGGESTION_WINDOW >= suggestions.length
        ? 0
        : prev + SUGGESTION_WINDOW
    );
  };

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: "#FFFFFF99",
        borderRadius: "16px",
        boxShadow: "none",
        maxHeight: "80vh",
      }}
    >
      <CardContent
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          maxHeight: "80vh",
        }}
      >
        <Typography
          fontWeight={400}
          fontSize={14}
          className={DashboardStyles.askAiText}
        >
          Ask AI
        </Typography>

        <Box sx={{ flex: 1, overflow: "auto", mb: 2 }} ref={chatContainerRef}>
          {messages.length === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="100%"
            >
              <img src={EmptyChatSvg} alt="" />
              <Typography color="#40444D" fontWeight={400} fontSize={12}>
                Only selected sources will be used for generating responses.
              </Typography>
            </Box>
          ) : (
            <List>
              {messages.map((message) => (
                <ListItem
                  key={message.id}
                  sx={{
                    flexDirection: message.isUser ? "row-reverse" : "row",
                    alignItems: "flex-start",
                    px: 0,
                  }}
                >
                  {!message.isUser && (
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        mx: 1,
                        background: `
                          linear-gradient(0deg, #3474E0, #3474E0),
                          radial-gradient(49.53% 50% at 50% 50%, #0051FF 0%, rgba(46, 185, 255, 0) 100%)
                        `,
                        boxShadow: `
                          -2px -2px 4px 0px #3FFF9887 inset,
                          1px 1px 4px 0px #FFFFFFCC inset
                        `,
                      }}
                    >
                      <img
                        src={ChatBotSvg}
                        alt=""
                        style={{ width: "10px", height: "10px" }}
                      />
                    </Avatar>
                  )}
                  <Box
                    sx={{
                      maxWidth: "70%",
                      bgcolor: message.isUser ? "#F2F4F7" : "#AEC8F51A",
                      borderRadius: 2,
                      p: 1.5,
                    }}
                  >
                    <Typography
                      color="#282B31"
                      fontWeight={400}
                      fontSize={12}
                      sx={{ whiteSpace: "pre-line" }}
                    >
                      {message.text}
                    </Typography>
                  </Box>
                </ListItem>
              ))}
              {isLoading && (
                <ListItem
                  sx={{
                    flexDirection: "row",
                    alignItems: "flex-start",
                    px: 0,
                  }}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      mx: 1,
                      background: `
          linear-gradient(0deg, #3474E0, #3474E0),
          radial-gradient(49.53% 50% at 50% 50%, #0051FF 0%, rgba(46, 185, 255, 0) 100%)
        `,
                      boxShadow: `
          -2px -2px 4px 0px #3FFF9887 inset,
          1px 1px 4px 0px #FFFFFFCC inset
        `,
                    }}
                  >
                    <img
                      src={ChatBotSvg}
                      alt=""
                      style={{ width: "10px", height: "10px" }}
                    />
                  </Avatar>
                  <div className={DashboardStyles.botMessageLoader}>
                    {new Array(3).fill("").map((_, index) => (
                      <div
                        key={`research-dot-${index}`}
                        className={DashboardStyles.dot}
                        style={{ animationDelay: `${index * 0.7}s` }}
                      ></div>
                    ))}
                  </div>
                </ListItem>
              )}
            </List>
          )}
        </Box>

        {/* Chat Suggestions */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            mb: 1,
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
        >
          {visibleSuggestions.map((suggestion, index) => (
            <Button
              key={index}
              variant="outlined"
              onClick={() => handleSuggestionClick(suggestion)}
              disabled={isLoading}
              style={{
                borderRadius: "24px",
                fontWeight: "500",
                fontSize: "10px",
                padding: "4px 6px",
                textTransform: "none",
                border: "1px solid #3474E0",
                color: "#3474E0",
              }}
            >
              {suggestion}
            </Button>
          ))}
          {suggestions.length > SUGGESTION_WINDOW && (
            <Button
              variant="text"
              onClick={handleShowMore}
              style={{
                fontWeight: "400",
                fontSize: "10px",
                minWidth: "auto",
                padding: "6px 12px",
                color: "#3474E0",
              }}
            >
              Show More
            </Button>
          )}
        </Box>

        <Box display="flex" gap={1}>
          <TextField
            fullWidth
            multiline
            maxRows={3}
            placeholder="Search or Ask AI"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyUp={handleKeyPress}
            disabled={isLoading}
            variant="outlined"
            size="small"
            className={DashboardStyles.textField}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "100px",
                fontWeight: "400",
                fontSize: "12px",
              },
            }}
          />
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={!inputValue.trim() || isLoading}
            style={{
              padding: "0",
              borderRadius: "100px",
              background: !inputValue.trim()
                ? "#E0E0E0"
                : `
                  linear-gradient(0deg, #3474E0, #3474E0),
                  radial-gradient(49.53% 50% at 50% 50%, #0051FF 0%, rgba(46, 185, 255, 0) 100%)
                `,
              boxShadow: !inputValue.trim()
                ? "none"
                : `
                  -2px -2px 4px 0px #3FFF9887 inset,
                  1px 1px 4px 0px #FFFFFFCC inset
                `,
              color: !inputValue.trim() ? "#A0A0A0" : "white",
            }}
          >
            <Send />
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default Chat;
